import { useEffect, useState, useCallback } from "react";
import { Sparkles, MapPin, Loader2, RefreshCw, Search, ChevronDown, ChevronUp } from "lucide-react";
import apiClient from "@/lib/api";
import { NEARBY_TYPE_LABELS, NEARBY_TYPE_OPTIONS } from "@/components/property/nearbyTypes";
import SearchableSelect from "@/components/ui/searchable-select";

const RADIUS_OPTIONS = [
  { value: 0.5, label: "500 m" },
  { value: 1, label: "1 km" },
  { value: 2, label: "2 km" },
  { value: 5, label: "5 km" },
  { value: 10, label: "10 km" },
];

// canRetry: only the owner (property form) may trigger a fresh Overpass discovery.
export default function NearbyAuto({ propertyId, defaultRadius = 5, canRetry = false }) {
  const [radius, setRadius] = useState(defaultRadius);
  const [type, setType] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [discovery, setDiscovery] = useState(null); // none|pending|in_progress|done|failed
  const [retrying, setRetrying] = useState(false);
  const INITIAL = 5;
  const [expanded, setExpanded] = useState(false);
  const [justUpdated, setJustUpdated] = useState(false);

  // Read-only: only reads nearby_places via detect endpoint. Never calls Overpass.
  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams({ radius_km: String(radius) });
      if (type) params.append("type", type);
      const { data } = await apiClient.get(`/properties/${propertyId}/nearby?${params.toString()}`);
      setResults(data.results || []);
      setExpanded(false);
      setDiscovery(data.discovery_status || "none");
    } catch {
      setResults([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [propertyId, radius, type]);

  useEffect(() => { load(); }, [load]);

  // Poll discovery status while a background discovery is running.
  useEffect(() => {
    if (discovery !== "in_progress" && discovery !== "pending") return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/properties/${propertyId}/nearby/status`);
        if (!active) return;
        if (data.status === "done" || data.status === "failed"
            || data.status === "empty" || data.status === "invalid_coords") {
          if (data.status === "done" && (data.count || 0) > 0) setJustUpdated(true);
          load(); // refetch results; load() will set discovery from the detect response
        } else {
          setDiscovery(data.status);
        }
      } catch { /* keep polling */ }
    };
    const iv = setInterval(poll, 3500);
    return () => { active = false; clearInterval(iv); };
  }, [discovery, propertyId, load]);

  const triggerRetry = async () => {
    setRetrying(true);
    try {
      await apiClient.post(`/properties/${propertyId}/nearby/discover`);
      setDiscovery("in_progress");
    } catch {
      setDiscovery("failed");
    } finally {
      setRetrying(false);
    }
  };

  const isDiscovering = discovery === "in_progress" || discovery === "pending";

  useEffect(() => {
    if (!justUpdated) return;
    const t = setTimeout(() => setJustUpdated(false), 4000);
    return () => clearTimeout(t);
  }, [justUpdated]);

  const renderEmpty = () => {
    if (isDiscovering) {
      return (
        <div className="rounded-xl border border-border bg-secondary/30 p-6 text-center" data-testid="nearby-auto-discovering">
          <Loader2 className="mx-auto animate-spin text-primary mb-2" size={22} />
          <p className="text-sm text-muted-foreground">Sedang mencari tempat di sekitar lokasi...</p>
        </div>
      );
    }
    if (discovery === "done") {
      return (
        <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center" data-testid="nearby-auto-empty">
          <p className="text-sm text-muted-foreground">
            Tidak ada tempat yang ditemukan dalam radius {radius} km. Coba perbesar radius.
          </p>
        </div>
      );
    }
    if (discovery === "empty" || discovery === "invalid_coords") {
      return (
        <div className="rounded-xl border border-amber-300/50 bg-amber-50 p-6 text-center" data-testid="nearby-auto-invalid-coords">
          <p className="text-sm text-amber-700 font-medium">
            Lokasi properti belum valid. Silakan tentukan lokasi melalui peta.
          </p>
          {canRetry && (
            <p className="mt-1 text-xs text-amber-700/80">Buka Edit Properti, geser pin ke lokasi yang benar, lalu simpan.</p>
          )}
        </div>
      );
    }
    if (discovery === "failed") {
      return (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center" data-testid="nearby-auto-discovery-failed">
          <p className="text-sm text-destructive">Gagal memperbarui tempat di sekitar. Silakan coba lagi.</p>
          {canRetry && (
            <button type="button" onClick={triggerRetry} disabled={retrying}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="nearby-auto-retry">
              {retrying ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Coba lagi
            </button>
          )}
        </div>
      );
    }
    // discovery === "none" (area belum pernah di-discover)
    return (
      <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center" data-testid="nearby-auto-none">
        <p className="text-sm text-muted-foreground">
          Data tempat di sekitar lokasi belum tersedia.{" "}
          {canRetry ? "Klik \"Perbarui data tempat\" untuk mencari." : "Silakan coba Cari Tempat Terdekat."}
        </p>
        {canRetry && (
          <button type="button" onClick={triggerRetry} disabled={retrying}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="nearby-auto-discover">
            {retrying ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Perbarui data tempat
          </button>
        )}
      </div>
    );
  };

  return (
    <div data-testid="nearby-auto">
      <div className="flex items-start gap-2 rounded-xl bg-secondary/40 px-4 py-3 mb-4">
        <Sparkles size={16} className="text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">Otomatis oleh AVASA.</span> Tempat penting di sekitar
          lokasi property dideteksi otomatis dari koordinat — owner tidak perlu memilih manual.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="nearby-auto-radius" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
          className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none">
          {RADIUS_OPTIONS.map((r) => <option key={r.value} value={r.value}>Radius {r.label}</option>)}
        </select>
        <div className="w-44">
          <SearchableSelect
            testid="nearby-auto-type"
            value={type}
            onChange={setType}
            options={NEARBY_TYPE_OPTIONS}
            emptyOption={{ value: "", label: "Semua tipe" }}
            placeholder="Semua tipe"
            searchPlaceholder="Cari tipe..."
            triggerClassName="py-2"
          />
        </div>
        <button type="button" onClick={load} data-testid="nearby-auto-refresh"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Cari Tempat Terdekat
        </button>
      </div>

      {justUpdated && (
        <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-700" data-testid="nearby-auto-updated">
          Tempat di sekitar berhasil diperbarui.
        </div>
      )}

      {loading ? (
        <div className="py-6 grid place-items-center"><Loader2 className="animate-spin text-primary" size={22} /></div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-center" data-testid="nearby-auto-error">
          <p className="text-sm text-destructive">Gagal memuat tempat terdekat.</p>
          <button type="button" onClick={load} className="mt-2 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : results.length === 0 ? (
        renderEmpty()
      ) : (
        <div className="space-y-2" data-testid="nearby-auto-list">
          {(expanded ? results : results.slice(0, INITIAL)).map((r, i) => (
            <div key={r.id} data-testid={`nearby-auto-item-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
              <div className="min-w-0 flex items-center gap-2">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-secondary text-primary shrink-0"><MapPin size={15} /></span>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{NEARBY_TYPE_LABELS[r.type] || r.type}</p>
                </div>
              </div>
              <span className="text-sm font-semibold text-primary shrink-0" data-testid={`nearby-auto-distance-${i}`}>{r.distance_text}</span>
            </div>
          ))}
          {results.length > INITIAL && (
            <button type="button" onClick={() => setExpanded((v) => !v)}
              data-testid="nearby-auto-show-more"
              className="w-full mt-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-primary hover:bg-secondary/60 transition-colors">
              {expanded ? (
                <><ChevronUp size={15} /> Tampilkan lebih sedikit</>
              ) : (
                <><ChevronDown size={15} /> Tampil lebih banyak ({results.length - INITIAL} lagi)</>
              )}
            </button>
          )}
        </div>
      )}

      <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground" data-testid="osm-attribution">
        Data tempat bersumber dari{" "}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer"
          className="underline hover:text-primary">© OpenStreetMap contributors</a>{" "}
        · Lisensi{" "}
        <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noreferrer"
          className="underline hover:text-primary">ODbL</a>
      </p>
    </div>
  );
}
